import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { decodedJWTPayload } from "../../utils/commonUtil";

import EditIcon from "../../static/image/editIcon.svg";
import DeleteIcon from "../../static/image/deleteIcon.svg";
import ProductDetailEdit from "./ProductDetailEdit";
import CommonDeleteModal from "../../components/commonDeleteModal/CommonDeleteModal";

import {
  API_STATE,
  FORM_TYPE,
  LANGUAGE_DEFAULT_OPTION,
  STATUS_CODE_RESPONSE_OK,
} from "../../utils/constant";

import { deleteProducts, fetchProducts } from "../../redux/productsSlice";
import { fetchLanguageOptions } from "../../redux/managePackageSlice";
import CommonSuccessModal from "../../components/commonSuccessModal/CommonSuccessModal";
import { isEmpty } from "../../utils/commonUtil";

function ManageProduct() {
  const dispatch = useDispatch();

  const {
    data: productList,
    status,
    error,
  } = useSelector((state) => state.productList);

  const { languageOptions: languageOptionsFromRedux, languageOptionsLoading } = useSelector((state) => state.managePackage);

  const [language, setLanguage] = useState(LANGUAGE_DEFAULT_OPTION);
  const [editingProduct, setEditingProduct] = useState(null);
  const [deleteModalShow, setDeleteModalShow] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [isShowAlert, setIsShowAlert] = useState(null);
  const [isAlertSuccess, setIAlertSuccess] = useState(null);
  const [permission, setPermission] = useState('');

  const formType = useRef(null);

  useEffect(() => {
    dispatch(fetchProducts(language));
    dispatch(fetchLanguageOptions());

    const token = localStorage.getItem('access_token');
    if (token) {
      const payload = decodedJWTPayload(token);
      if (payload?.mn_id) {
        const modulePermission = payload.mn_id.split('|')
          .find(item => item.startsWith('020200-'));
        if (modulePermission) {
          setPermission(modulePermission.split('-')[1]);
        }
      }
    }
  }, []);

  const getLanguageOptions = () => {
    if (!languageOptionsFromRedux?.resdata?.data) return [];

    const options = [];
    const languageData = languageOptionsFromRedux.resdata.data;

    for (const langCode in languageData) {
      if (Array.isArray(languageData[langCode])) {
        languageData[langCode].forEach(item => {
          if (item.type === 'LANGUAGE' && item.codes?.length) {
            item.codes.forEach(codeItem => {
              options.push({
                value: codeItem.code,
                label: codeItem.value
              });
            });
          }
        });
      }
    }

    return options;
  };

  const languageOptions = getLanguageOptions();

  const toggleLanguage = (selectedLanguage) => {
    setLanguage(selectedLanguage);
    dispatch(fetchProducts(selectedLanguage));
  };

  const handleEdit = (id) => {
    if (permission.includes('W')) {
      setEditingProduct(id);
      formType.current = FORM_TYPE.EDIT;
    }
  };

  const handleAddNew = () => {
    if (permission === 'RWX') {
      setEditingProduct(FORM_TYPE.CREATE);
      formType.current = FORM_TYPE.CREATE;
    }
  };

  const handleResetProducts = () => {
    setEditingProduct(null);
    formType.current = null;
    dispatch(fetchProducts(language));
  };

  const handleBack = () => {
    setEditingProduct(null);
    formType.current = null;
  };

  const handleDeleteClick = (product) => {
    if (permission.includes('X')) {
      setProductToDelete(product.uuid);
      setDeleteModalShow(true);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalShow(false);
    setProductToDelete(null);
  };

  const handleDeleteConfirm = () => {
    if (productToDelete && permission.includes('X')) {
      const response = dispatch(deleteProducts(productToDelete));
      response
        .then((res) => {
          if (res?.payload?.status === STATUS_CODE_RESPONSE_OK) {
            dispatch(fetchProducts(language));
          }
          if (res?.error) {
            handleAlertShow(res.error.message, false);
          }

          setDeleteModalShow(false);
          setProductToDelete(null);
        })
        .catch((err) => {
          handleAlertShow(err, false);
          setDeleteModalShow(false);
          setProductToDelete(null);
        });
    }
  };

  const handleAlertShow = (message, isSuccess) => {
    setIsShowAlert(message);
    setIAlertSuccess(isSuccess);
  };

  const handleAlertClose = () => {
    setIsShowAlert(null);
    setIAlertSuccess(null);
  };

  if (editingProduct) {
    return (
      <ProductDetailEdit
        productId={editingProduct}
        formType={formType.current}
        initLanguage={language}
        handleComplete={handleResetProducts}
        handleBack={handleBack}
      />
    );
  }

  return (
    <div className="create-user-container">
      <div className="package-wrapper">
        <div
          className="package-language-container"
          style={{ position: "relative", top: "20px" }}
        >
          <select
            className="language-select"
            value={language}
            onChange={(e) => toggleLanguage(e.target.value)}
            style={{
              padding: '1px 12px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '14px',
              minWidth: '60px',
              minHeight: '30px',
              outline: 'none',
            }}
            disabled={languageOptionsLoading}
          >
            {languageOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="package-container">
          <div className="package-header">Product</div>

          {status === API_STATE.LOADING && (
            <div className="loading-packages">Loading products...</div>
          )}

          {status === API_STATE.FAILED && (
            <div className="error-packages">
              Error loading products: {error}
            </div>
          )}

          {status === API_STATE.SUCCEEDED && (
            <>
              {productList.map((product, index) => (
                <div key={product.uuid} className={`package-item`}>
                  <div className="package-content">
                    <div className="package-info">
                      <div className="package-label">
                        Product name:&nbsp;
                        <span className="package-value">{product.name}</span>
                      </div>
                      <div className="package-label">
                        Product description:&nbsp;
                        <span className="package-value">
                          {product.description}
                        </span>
                      </div>
                    </div>
                    <div className="package-actions">
                      <button
                        className="icon-btn"
                        onClick={() => handleEdit(product.uuid)}
                        style={{
                          opacity: permission.includes('W') ? 1 : 0.5,
                          cursor: permission.includes('W') ? 'pointer' : 'default',
                          pointerEvents: permission.includes('W') ? 'auto' : 'none'
                        }}
                      >
                        <img
                          src={EditIcon}
                          alt="Edit"
                          className="action-icon"
                        />
                      </button>
                      <button
                        className="icon-btn"
                        onClick={() => handleDeleteClick(product)}
                        style={{
                          opacity: permission.includes('X') ? 1 : 0.5,
                          cursor: permission.includes('X') ? 'pointer' : 'default',
                          pointerEvents: permission.includes('X') ? 'auto' : 'none'
                        }}
                      >
                        <img
                          src={DeleteIcon}
                          alt="Delete"
                          className="action-icon"
                        />
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {productList.length === 0 && (
                <div className="empty-packages">
                  No products found. {permission === 'RWX' && 'Click "Add new" to create your first product.'}
                </div>
              )}
            </>
          )}
        </div>

        {language === "EN" && (
          <button
            className="package-add-btn"
            onClick={handleAddNew}
            style={{
              opacity: permission === 'RWX' ? 1 : 0.5,
              cursor: permission === 'RWX' ? 'pointer' : 'default',
              pointerEvents: permission === 'RWX' ? 'auto' : 'none'
            }}
          >
            Add new
          </button>
        )}
      </div>
      {/* 
      <CommonSuccessModal
        showModal={!isEmpty(isShowAlert)}
        handleClose={handleAlertClose}
        message={isShowAlert}
        status={isAlertSuccess}
      /> */}

      <CommonDeleteModal
        show={deleteModalShow}
        onHide={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Product"
        instruction="Are you sure you want to delete this product?"
      />
    </div>
  );
}

export default ManageProduct;
